import { AutoSuggestAddress, Col, ComponentLoader, FormRadioGroup, Row } from '@snsw/react-component-library';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { AddressService } from './AddressService';
import { InternationalAddress } from './InternationalAddress';

export const LocalAndInternationalAddress = React.forwardRef(
  (
    {
      baseUrl,
      apiRoute = '/api/addresses',
      id,
      value = {},
      onChange = () => {},
      onErrorChange = () => {},
      localAddressValidator = {},
      internationalAddressValidator = {},
      helpMessage = '',
      ...rest
    },
    ref,
  ) => {
    const {
      addressType: savedAddressType = '',
      addressUi: savedAddressUi = {},
      address: savedAddress = {},
      addressMode: savedAddressMode = 'auto',
    } = value;

    const [addressType, setAddressType] = useState(savedAddressType);
    const [addressDetails, setAddressDetails] = useState(addressType === 'local' ? savedAddress : {});
    const [addressUi, setAddressUi] = useState(savedAddressUi);
    const [addressUiWithErrors, setAddressUiWithErrors] = useState(_.cloneDeep(savedAddressUi));

    const [addressMode, setAddressMode] = useState(savedAddressMode);
    const [internationalAddress, setInternationalAddress] = useState(
      addressType === 'international' ? savedAddress : {},
    );

    const [errors, setErrors] = useState({});
    const [showErrors, setShowErrors] = useState(false);
    const [errorSummary, setErrorSummary] = useState([]);
    const [internationalAddErrorSummary, setInternationalAddErrorSummary] = useState([]);
    const [loadingAddress, setLoadingAddress] = useState(false);

    const addressTypeOptions = [
      {
        value: 'local',
        label: 'Australia',
      },
      {
        value: 'international',
        label: 'Outside Australia',
      },
    ];

    const { getAddressSuggestions, getAddressDetailsById } = AddressService(baseUrl + apiRoute);

    const getDetailsById = async (addressId) => {
      setLoadingAddress(true);
      const address = await getAddressDetailsById(addressId);
      setAddressDetails(address);
      setLoadingAddress(false);

      return address?.RawAddress ? address.RawAddress : address;
    };

    const onSelect = (address, key, isComplete) => {
      setAddressUiWithErrors(_.cloneDeep(address));
      setAddressUi(address);
    };

    const onChangeAddress = (address, key, isComplete) => {
      setAddressUiWithErrors(_.cloneDeep(address));
      setAddressUi(address);
    };

    const onBlur = (address, key, isComplete, inputValue, highlightedValue) => {
      setAddressUiWithErrors(_.cloneDeep(address));
      setAddressUi(address);
    };

    const onSwitchMode = (value) => {
      if (value === 'manual') {
        setAddressDetails({});
      }
      setAddressUi({});
      setAddressMode(value);
    };

    const addressValidator = {
      validateStreet: (street) => ({ status: true, errorMessage: '' }),
      validateSuburb: (suburb) => ({ status: true, errorMessage: '' }),
      validateState: (state) => ({ status: true, errorMessage: '' }),
      validatePostCode: (postCode) => ({ status: true, errorMessage: '' }),
      validateFormattedAddress: (formattedAddress) => ({ status: true, errorMessage: '' }),
      ...localAddressValidator,
    };

    const validators = [
      {
        fieldName: `addressType_${id}`,
        fieldValue: addressType,
        isValid: (v) => !isEmpty(v),
        errorMessage: () => 'Please select an option',
      },
      {
        fieldName: `australiaAddress_${id}-manual-address-form-group-street-address`,
        fieldValue: addressUi?.streetAddress?.value,
        isValid: (v) =>
          addressType === 'local' && addressMode === 'manual'
            ? addressValidator.validateStreet(v)
            : { status: true, errorMessage: '' },
      },
      {
        fieldName: `australiaAddress_${id}-manual-address-form-group-suburb`,
        fieldValue: addressUi?.suburb?.value,
        isValid: (v) =>
          addressType === 'local' && addressMode === 'manual'
            ? addressValidator.validateSuburb(v)
            : { status: true, errorMessage: '' },
      },
      {
        fieldName: `australiaAddress_${id}-manual-address-form-group-state`,
        fieldValue: addressUi?.state?.value,
        isValid: (v) =>
          addressType === 'local' && addressMode === 'manual'
            ? addressValidator.validateState(v)
            : { status: true, errorMessage: '' },
      },
      {
        fieldName: `australiaAddress_${id}-manual-address-form-group-postcode`,
        fieldValue: addressUi?.postcode?.value,
        isValid: (v) =>
          addressType === 'local' && addressMode === 'manual'
            ? addressValidator.validatePostCode(v)
            : { status: true, errorMessage: '' },
      },
      {
        fieldName: `australiaAddress_${id}-auto-suggest-formgroup`,
        fieldValue: addressUi,
        isValid: (v) =>
          addressType === 'local' && addressMode === 'auto'
            ? addressValidator.validateFormattedAddress(v)
            : { status: true, errorMessage: '' },
      },
    ];

    const validateAll = () => {
      const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
      let internationalValid = true;
      if (internationalAddressRef.current && addressType === 'international')
        internationalValid = internationalAddressRef.current.validate();

      setShowErrors(!isCurrentComponentValid);

      return isCurrentComponentValid && internationalValid;
    };

    const internationalAddressRef = React.useRef();

    if (ref) {
      ref.current = {
        validate: () => {
          return validateAll();
        },
      };
    }

    useEffect(() => {
      setAddressUiWithErrors((prevState) => {
        const newState = { suburb: {}, state: {}, postcode: {}, streetAddress: {}, formattedAddress: {} };
        const prevStateCloned = _.cloneDeep(prevState);
        Object.assign(newState, prevStateCloned);

        newState.streetAddress.errorMessage =
          errors[`australiaAddress_${id}-manual-address-form-group-street-address`]?.errorMessage;

        newState.suburb.errorMessage = errors[`australiaAddress_${id}-manual-address-form-group-suburb`]?.errorMessage;

        newState.state.errorMessage = errors[`australiaAddress_${id}-manual-address-form-group-state`]?.errorMessage;

        newState.postcode.errorMessage =
          errors[`australiaAddress_${id}-manual-address-form-group-postcode`]?.errorMessage;

        newState.formattedAddress.errorMessage = errors[`australiaAddress_${id}-auto-suggest-formgroup`]?.errorMessage;

        return newState;
      });
    }, [errors]);

    useEffect(() => {
      onErrorChange(errorSummary.concat(internationalAddErrorSummary));
    }, [errorSummary, internationalAddErrorSummary]);

    useEffect(() => {
      const address = {
        ...(!isEmpty(addressType) && {
          addressType,
        }),
        ...(addressType === 'local' && {
          addressMode,
          addressUi,
        }),
        ...(addressType === 'local' &&
          addressMode === 'auto' && {
            address: addressDetails,
          }),
        ...(addressType === 'international' && {
          address: internationalAddress,
        }),
      };

      onChange(address);

      if (showErrors) validateExt(validators, setErrors, setErrorSummary);
    }, [addressType, addressMode, addressUi, addressDetails, internationalAddress]);

    return (
      <>
        <FormRadioGroup
          {...rest}
          id={`addressType_${id}`}
          name={`addressType_${id}`}
          vertical={true}
          options={addressTypeOptions}
          onChange={(v) => {
            if (v === 'local') {
              setInternationalAddErrorSummary([]);
            }

            setAddressType(v);
          }}
          value={addressType}
          errorMessage={errors[`addressType_${id}`]?.errorMessage}
          hasError={showErrors && errors[`addressType_${id}`]?.invalid}
        />

        {addressType === 'local' && (
          <AutoSuggestAddress
            id={`australiaAddress_${id}`}
            value={addressUiWithErrors}
            onSelect={onSelect}
            onChange={onChangeAddress}
            onBlur={onBlur}
            label=""
            helpMessage={helpMessage}
            onSwitch={onSwitchMode}
            initialMode={addressMode}
            backendAPI={{
              getSuggestions: getAddressSuggestions,
              getDetailsById,
            }}
          />
        )}

        {addressType === 'international' && (
          <InternationalAddress
            id={`international_${id}`}
            ref={internationalAddressRef}
            onChange={setInternationalAddress}
            value={internationalAddress}
            onErrorChange={setInternationalAddErrorSummary}
            customValidator={internationalAddressValidator}
          />
        )}

        {loadingAddress && (
          <Row>
            <Col span={12}>
              <ComponentLoader fullPage />
            </Col>
          </Row>
        )}
      </>
    );
  },
);

LocalAndInternationalAddress.displayName = 'LocalAndInternationalAddress';
